import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaArrowLeft, FaArrowRight, FaExpand, FaPlay } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { data } from '../data';
import projectGroups from '../data/projectGroups';

const portfolioCategories = {
    en: [
        { id: 'channel-letters', name: 'Channel Letters' },
        { id: 'ada-signs', name: 'ADA Signs' },
        { id: 'vehicle-graphics', name: 'Cars & Vehicle Graphics' },
        { id: 'acrylic-signs', name: 'Acrylic & Interior Signs' },
        { id: 'storefront-signage', name: 'Storefront Signage' },
        { id: 'banners-printing', name: 'Banners & Printing' },
        { id: 'design-branding', name: 'Design & Branding' }
    ],
    es: [
        { id: 'channel-letters', name: 'Channel Letters' },
        { id: 'ada-signs', name: 'Señalización ADA' },
        { id: 'vehicle-graphics', name: 'Carros y gráficos vehiculares' },
        { id: 'acrylic-signs', name: 'Acrílicos y rótulos interiores' },
        { id: 'storefront-signage', name: 'Rótulos y vitrinas comerciales' },
        { id: 'banners-printing', name: 'Banners e impresión' },
        { id: 'design-branding', name: 'Diseño e identidad visual' }
    ]
};

const getCategory = (project) => {
    if (project.category === 'vehicular-advertising') return 'vehicle-graphics';
    if (project.category === 'digital-design') return 'design-branding';
    if (project.category === 'interior-decoration') return 'acrylic-signs';
    if (project.category === 'exterior-signage') return 'storefront-signage';
    if (project.category === 'printed-advertising') return 'banners-printing';
    if (project.category === 'corporate-identity') {
        return project.title.toLowerCase().includes('window') || project.title.toLowerCase().includes('ventana')
            ? 'storefront-signage'
            : 'acrylic-signs';
    }
    if (project.category === 'video-portfolio') {
        const title = project.title.toLowerCase();
        if (title.includes('vehicle') || title.includes('vehículo') || title.includes('sticker') || title.includes('calcomanía')) return 'vehicle-graphics';
        if (title.includes('banner') || title.includes('canvas') || title.includes('lona') || title.includes('plotter')) return 'banners-printing';
    }
    return 'storefront-signage';
};

const Portfolio = () => {
    const { language } = useLanguage();
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [selectedImage, setSelectedImage] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [loadedImages, setLoadedImages] = useState(new Set());
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // Obtener datos del portafolio según el idioma
    const portfolioData = data.portfolio[language];
    const { portfolioTitle, portfolioSubtitle, projects: legacyProjects } = portfolioData;
    const categories = portfolioCategories[language];
    const featuredProjectGroups = projectGroups
        .filter((project) => project.slug.includes('channel-letter') || project.portfolioCategory)
        .map((project) => {
            const media = project.media[0];
            return {
                slug: project.slug,
                title: project.title,
                description: project.summary[language],
                category: project.portfolioCategory || 'channel-letters',
                image: media.type === 'image' ? media.src : media.poster,
                video: media.type === 'video' ? media.src : null,
                thumbnail: media.poster
            };
        });
    const projects = [
        ...featuredProjectGroups,
        ...legacyProjects.map((project) => ({ ...project, category: getCategory(project) }))
    ];

    // Filtrar proyectos según categoría seleccionada
    const filteredProjects = selectedCategory === 'all' 
        ? projects 
        : projects.filter(project => project.category === selectedCategory);

    const handleImageLoad = (src) => {
        setLoadedImages(prev => new Set([...prev, src]));
    };

    const isImageLoaded = (src) => {
        return loadedImages.has(src);
    };

    const openModal = (image, index) => {
        setSelectedImage(image);
        setCurrentIndex(index);
    };

    const closeModal = useCallback(() => {
        setSelectedImage(null);
        setCurrentIndex(0);
    }, []);

    const nextImage = useCallback(() => {
        const nextIndex = (currentIndex + 1) % filteredProjects.length;
        setCurrentIndex(nextIndex);
        setSelectedImage(filteredProjects[nextIndex]);
    }, [currentIndex, filteredProjects]);

    const prevImage = useCallback(() => {
        const prevIndex = (currentIndex - 1 + filteredProjects.length) % filteredProjects.length;
        setCurrentIndex(prevIndex);
        setSelectedImage(filteredProjects[prevIndex]);
    }, [currentIndex, filteredProjects]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Escape') closeModal();
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') prevImage();
    }, [closeModal, nextImage, prevImage]);

    // Funciones para manejar touch/swipe
    const onTouchStart = (e) => {
        setTouchEnd(null); // de lo contrario el evento swipe se dispara cuando scrolleas hacia abajo
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            nextImage(); // Swipe left = siguiente imagen
        }
        if (isRightSwipe) {
            prevImage(); // Swipe right = imagen anterior
        }
    };

    // Effect para manejar teclas cuando el modal está abierto
    useEffect(() => {
        if (selectedImage) {
            const handleKeydown = (e) => handleKeyPress(e);
            document.addEventListener('keydown', handleKeydown);
            return () => document.removeEventListener('keydown', handleKeydown);
        }
    }, [selectedImage, handleKeyPress]);

    return (
        <div className={`min-h-screen ${isDark
            ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-black'
            : 'bg-gradient-to-br from-white via-green-yellow-50 to-atlantis/10'
            } pt-20`}>
            {/* Header */}
            <div className="relative py-20 px-4 overflow-hidden">
                <div className={`absolute inset-0 ${isDark
                    ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-black'
                    : 'bg-gradient-to-br from-saratoga via-atlantis to-green-yellow-primary'
                    }`}></div>
                <div className={`absolute inset-0 ${isDark
                    ? 'bg-gradient-to-t from-atlantis/20 to-transparent'
                    : 'bg-gradient-to-t from-marshland/20 to-transparent'
                    }`}></div>

                <div className="max-w-7xl mx-auto relative z-10 text-center">
                    <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 animate-fade-in drop-shadow-lg">
                        {portfolioTitle}
                    </h1>
                    <div className="w-24 h-1 bg-gradient-to-r from-white to-green-yellow-100 mx-auto mb-8"></div>
                    <p className="text-xl text-white/90 max-w-3xl mx-auto drop-shadow-md leading-relaxed">
                        {portfolioSubtitle}
                    </p>
                </div>
            </div>

            {/* Galería */}
            <div className="max-w-7xl mx-auto px-4 py-20">
                {/* Filtros de Categoría */}
                <div className="flex flex-wrap justify-center gap-4 mb-12">
                    <button
                        onClick={() => setSelectedCategory('all')}
                        aria-pressed={selectedCategory === 'all'}
                        className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${
                            selectedCategory === 'all'
                                ? 'bg-atlantis text-white dark:text-black shadow-lg'
                                : isDark
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-white text-saratoga hover:bg-gray-50 shadow-md'
                        }`}
                    >
                        {language === 'es' ? 'Todos los trabajos' : 'All work'} ({projects.length})
                    </button>
                    {categories.map((category) => (
                        <button
                            key={category.id}
                            onClick={() => setSelectedCategory(category.id)}
                            aria-pressed={selectedCategory === category.id}
                            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${
                                selectedCategory === category.id
                                    ? 'bg-atlantis text-white dark:text-black shadow-lg'
                                    : isDark
                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    : 'bg-white text-saratoga hover:bg-gray-50 shadow-md'
                            }`}
                        >
                            {category.name} ({projects.filter((project) => project.category === category.id).length})
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredProjects.map((image, index) => (
                        <div
                            key={index}
                            className={`group relative overflow-hidden rounded-2xl cursor-pointer transform transition-all duration-300 hover:scale-105 hover:shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'
                                }`}
                            onClick={() => image.slug ? navigate(`/portfolio/${image.slug}`) : openModal(image, index)}
                        >
                            <div className="relative overflow-hidden">
                                {image.video ? (
                                    // Video thumbnail con botón de play
                                    <div className="relative">
                                        {image.thumbnail ? (
                                            <img
                                                src={image.thumbnail}
                                                alt={image.title}
                                                className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <div className={`w-full h-64 flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'
                                                }`}>
                                                <FaPlay className="text-6xl text-atlantis" />
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-all duration-300 flex items-center justify-center">
                                            <div className="bg-white/90 rounded-full p-4 group-hover:scale-110 transition-transform duration-300">
                                                <FaPlay className="text-atlantis text-3xl ml-1" />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    // Imagen regular
                                    <>
                                        {!isImageLoaded(image.image) ? (
                                            // Placeholder mientras carga
                                            <div className={`w-full h-64 flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-200'
                                                }`}>
                                                <div className="text-center">
                                                    <div className="w-12 h-12 mx-auto mb-2 relative">
                                                        <div className="absolute inset-0 border-2 border-atlantis/30 rounded-full"></div>
                                                        <div className="absolute inset-0 border-2 border-transparent border-t-atlantis rounded-full animate-spin" style={{ animationDuration: '1s' }}></div>
                                                        <div className="absolute inset-1 bg-white rounded-full flex items-center justify-center shadow-sm">
                                                            <img
                                                                src="/logo.jpg"
                                                                alt="Loading"
                                                                className="w-6 h-6 rounded-full object-cover"
                                                            />
                                                        </div>
                                                    </div>
                                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        Cargando...
                                                    </p>
                                                </div>
                                            </div>
                                        ) : null}
                                        <img
                                            src={image.image}
                                            alt={image.title}
                                            className={`w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110 ${isImageLoaded(image.image) ? 'opacity-100' : 'opacity-0'
                                                }`}
                                            loading="lazy"
                                            onLoad={() => handleImageLoad(image.image)}
                                        />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                                            <FaExpand className="text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="p-6">
                                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-atlantis">
                                    {categories.find((category) => category.id === image.category)?.name}
                                </p>
                                <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white ' : 'text-saratoga'
                                    }`}>
                                    {image.title}
                                </h3>
                                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-saratoga/70'
                                    }`}>
                                    {image.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Modal */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
                    onClick={closeModal}
                    tabIndex={0}
                >
                    <div 
                        className="relative max-w-4xl max-h-[90vh] mx-4" 
                        onClick={(e) => e.stopPropagation()}
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                    >
                        {/* Botón cerrar */}
                        <button
                            onClick={closeModal}
                            className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200"
                        >
                            <FaTimes />
                        </button>

                        {/* Navegación - Mejorada para móvil */}
                        {filteredProjects.length > 1 && (
                            <>
                                <button
                                    onClick={prevImage}
                                    className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200 touch-manipulation"
                                >
                                    <FaArrowLeft className="text-sm md:text-base" />
                                </button>
                                <button
                                    onClick={nextImage}
                                    className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-all duration-200 touch-manipulation"
                                >
                                    <FaArrowRight className="text-sm md:text-base" />
                                </button>
                            </>
                        )}

                        {/* Indicador de imagen actual */}
                        {filteredProjects.length > 1 && (
                            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                                {currentIndex + 1} / {filteredProjects.length}
                            </div>
                        )}

                        {/* Contenido principal - Imagen o Video */}
                        <div className="relative select-none">
                            {selectedImage.video ? (
                                // Video player
                                <video
                                    src={selectedImage.video}
                                    alt={selectedImage.title}
                                    className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                                    controls
                                    autoPlay
                                    muted
                                    playsInline
                                />
                            ) : (
                                // Imagen regular
                                <img
                                    src={selectedImage.image}
                                    alt={selectedImage.title}
                                    className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                                    draggable={false}
                                />
                            )}
                            
                            {/* Indicador de swipe para móvil */}
                            <div className="md:hidden absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/40 text-white px-3 py-1 rounded-full text-xs">
                                👈 Desliza para navegar 👉
                            </div>
                        </div>

                        {/* Información */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
                            <h3 className="text-xl font-bold text-white mb-2">{selectedImage.title}</h3>
                            <p className="text-white/80 text-sm mb-4">{selectedImage.description}</p>
                            
                            {/* Puntos indicadores */}
                            {filteredProjects.length > 1 && (
                                <div className="flex justify-center space-x-2">
                                    {filteredProjects.map((_, index) => (
                                        <button
                                            key={index}
                                            onClick={() => {
                                                setCurrentIndex(index);
                                                setSelectedImage(filteredProjects[index]);
                                            }}
                                            className={`w-2 h-2 rounded-full transition-all duration-200 ${
                                                index === currentIndex 
                                                    ? 'bg-atlantis scale-125' 
                                                    : 'bg-white/40 hover:bg-white/60'
                                            }`}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Portfolio;
